/**
 * TaskReviewSection
 *
 * Displays auto-generated tasks for the matter with inline editing
 * for assignee and due date. Tasks are grouped by time-based priority
 * (Immediate, Overdue, 24hrs, 5 days, 2 weeks, etc.)
 */
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { ClipboardList } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useTaskGrouping } from '@/components/tasks/useTaskGrouping';
import { cn, filterStaffUsers } from '@/lib/utils';
import type { DeliverableSchemaRead } from '@/services/api/deliverablesServicev2';
import { useMatterDeliverablesListQuery } from '@/services/api/deliverablesServicev2';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api/tasksService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import type { Task, TaskStatus } from '@/services/types/client-intake-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { TaskRow } from './TaskRow';

interface TaskReviewSectionProps {
  matterId: string;
  onTaskClick?: (taskId: string) => void;
  onDocumentClick?: (artifactId: string) => void;
  toggleInCallPrep?: (sourceId: string, text: string) => void;
  isInCallPrep?: (sourceId: string) => boolean;
}

const getPriorityGroupColor = (groupName: string) => {
  switch (groupName) {
    case 'Immediate':
      return { border: 'border-l-red-500', indicator: 'bg-red-500' };
    case 'Overdue':
      return { border: 'border-l-red-400', indicator: 'bg-red-400' };
    case 'Next Action Within 24 Hours':
      return { border: 'border-l-orange-500', indicator: 'bg-orange-500' };
    case 'Next Action Within 5 Days':
      return { border: 'border-l-amber-500', indicator: 'bg-amber-500' };
    case 'Next Action Within 2 Weeks':
      return { border: 'border-l-blue-500', indicator: 'bg-blue-500' };
    case 'Next Action Beyond 2 Weeks':
      return { border: 'border-l-zinc-400', indicator: 'bg-zinc-400' };
    default:
      return { border: 'border-l-zinc-300', indicator: 'bg-zinc-300' };
  }
};

interface TaskGroupSectionProps {
  groupName: string;
  tasks: Task[];
  isOpen: boolean;
  onToggle: () => void;
  staffUsers: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  }[];
  onAssigneeChange: (taskId: string, userId: string | null) => Promise<void>;
  onDueDateChange: (taskId: string, date: string | null) => Promise<void>;
  onPriorityChange: (taskId: string, order: number) => Promise<void>;
  onStatusChange: (
    taskId: string,
    newStatus: CanonicalTaskStatus,
  ) => Promise<void>;
  onTaskClick: (taskId: string) => void;
  onDocumentClick?: (artifactId: string) => void;
  toggleInCallPrep?: (sourceId: string, text: string) => void;
  isInCallPrep?: (sourceId: string) => boolean;
  deliverablesByTaskId?: Record<string, DeliverableSchemaRead[]>;
}

const TaskGroupSection = ({
  groupName,
  tasks,
  isOpen,
  onToggle,
  staffUsers,
  onAssigneeChange,
  onDueDateChange,
  onPriorityChange,
  onStatusChange,
  onTaskClick,
  onDocumentClick,
  toggleInCallPrep,
  isInCallPrep,
  deliverablesByTaskId,
}: TaskGroupSectionProps) => {
  const colors = getPriorityGroupColor(groupName);

  return (
    <div className="border-b border-zinc-200 dark:border-zinc-800 last:border-none">
      {/* Group Header */}
      <button
        type="button"
        className={cn(
          'flex items-center justify-between w-full px-3 py-2 text-left focus:outline-none transition-colors',
          colors.border,
          'border-l-2 bg-zinc-50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-800',
        )}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <ChevronRightIcon
            className={cn(
              'h-3 w-3 text-zinc-400 dark:text-zinc-500 transition-transform shrink-0',
              isOpen && 'rotate-90',
            )}
          />
          <span className="text-xs font-semibold text-zinc-900 dark:text-white truncate">
            {groupName}
          </span>
        </div>
        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 tabular-nums">
          {tasks.length}
        </span>
      </button>

      {/* Task List */}
      {isOpen && (
        <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              staffUsers={staffUsers}
              onAssigneeChange={onAssigneeChange}
              onDueDateChange={onDueDateChange}
              onPriorityChange={onPriorityChange}
              onStatusChange={onStatusChange}
              onTaskClick={onTaskClick}
              toggleInCallPrep={toggleInCallPrep}
              isInCallPrep={isInCallPrep?.(`task_${task.id}`)}
              taskDeliverables={deliverablesByTaskId?.[task.id]}
              onDocumentClick={onDocumentClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const TaskReviewSection = ({
  matterId,
  onTaskClick: onTaskClickProp,
  onDocumentClick,
  toggleInCallPrep,
  isInCallPrep,
}: TaskReviewSectionProps) => {
  const {
    data: tasksData,
    isLoading,
    isError,
  } = useGetTasksQuery({ matter: matterId }, { skip: !matterId });

  const { data: staffUsersRaw, isLoading: isStaffLoading } =
    useGetStaffUsersQuery();

  // Fetch task statuses to get completed/pending status IDs
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();

  // Fetch all matter deliverables for match indicators
  const { data: allDeliverables } = useMatterDeliverablesListQuery(
    { matterId },
    { skip: !matterId },
  );

  const [updateTask] = useUpdateTaskMutation();

  // Group deliverables by task_id for quick lookup
  const deliverablesByTaskId = useMemo(() => {
    if (!allDeliverables || !Array.isArray(allDeliverables)) return {};
    return allDeliverables.reduce<Record<string, DeliverableSchemaRead[]>>(
      (acc, deliverable) => {
        const taskId = typeof deliverable.task_id === 'string' ? deliverable.task_id : String(deliverable.task_id);
        if (!acc[taskId]) acc[taskId] = [];
        acc[taskId].push(deliverable);
        return acc;
      },
      {},
    );
  }, [allDeliverables]);

  // Find completed and pending statuses
  const completedStatus = useMemo(() => {
    return taskStatuses.find(
      (s: TaskStatus) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
  }, [taskStatuses]);

  const pendingStatus = useMemo(() => {
    return taskStatuses.find(
      (s: TaskStatus) => s.canonical_status === CanonicalTaskStatus.PENDING,
    );
  }, [taskStatuses]);

  const notApplicableStatus = useMemo(() => {
    return taskStatuses.find(
      (s: TaskStatus) =>
        s.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE,
    );
  }, [taskStatuses]);

  const allTasks = useMemo(() => {
    if (!tasksData?.results) return [];
    return tasksData.results.filter(
      (task) =>
        task.type?.canonical_type !== CanonicalTaskType.CONDUCT_INITIAL_REVIEW,
    );
  }, [tasksData?.results]);

  // Use the task grouping hook — keep resolved tasks in their time-based groups
  const { getFilteredAndSortedGroups, getInitialOpenState } =
    useTaskGrouping(allTasks, { keepResolvedInPlace: true });

  // Initialize open state for groups - completed group starts collapsed
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [hasInitialized, setHasInitialized] = useState(false);

  // Set initial open state when tasks load
  useEffect(() => {
    if (allTasks.length > 0 && !hasInitialized) {
      const initial = getInitialOpenState();
      setOpenGroups(initial);
      setHasInitialized(true);
    }
  }, [allTasks.length, getInitialOpenState, hasInitialized]);

  const staffUsers = useMemo(() => {
    return filterStaffUsers(staffUsersRaw || []);
  }, [staffUsersRaw]);

  const sortedGroups = useMemo(() => {
    return getFilteredAndSortedGroups(true);
  }, [getFilteredAndSortedGroups]);

  const handleToggleGroup = useCallback((groupName: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  }, []);

  const handleAssigneeChange = useCallback(
    async (taskId: string, userId: string | null) => {
      try {
        await updateTask({
          matterId,
          taskId,
          task: { assignee_id: userId },
        }).unwrap();
        toast.success('Assignee updated');
      } catch (error) {
        console.error('Failed to update assignee:', error);
        toast.error('Failed to update assignee');
      }
    },
    [matterId, updateTask],
  );

  const handleDueDateChange = useCallback(
    async (taskId: string, date: string | null) => {
      try {
        await updateTask({
          matterId,
          taskId,
          task: { due_date: date },
        }).unwrap();
        toast.success('Due date updated');
      } catch (error) {
        console.error('Failed to update due date:', error);
        toast.error('Failed to update due date');
      }
    },
    [matterId, updateTask],
  );

  const handlePriorityChange = useCallback(
    async (taskId: string, order: number) => {
      try {
        await updateTask({
          matterId,
          taskId,
          task: { order },
        }).unwrap();
        toast.success('Priority updated');
      } catch (error) {
        console.error('Failed to update priority:', error);
        toast.error('Failed to update priority');
      }
    },
    [matterId, updateTask],
  );

  const handleStatusChange = useCallback(
    async (taskId: string, newStatus: CanonicalTaskStatus) => {
      let targetStatus: TaskStatus | undefined;
      switch (newStatus) {
        case CanonicalTaskStatus.COMPLETED:
          targetStatus = completedStatus;
          break;
        case CanonicalTaskStatus.NOT_APPLICABLE:
          targetStatus = notApplicableStatus;
          break;
        case CanonicalTaskStatus.PENDING:
        default:
          targetStatus = pendingStatus;
          break;
      }

      if (!targetStatus) {
        toast.error('Unable to update task status');
        return;
      }

      try {
        await updateTask({
          matterId,
          taskId,
          task: {
            status_id: targetStatus.id,
            completed_at:
              newStatus === CanonicalTaskStatus.COMPLETED
                ? new Date().toISOString()
                : null,
          },
        }).unwrap();

        const messages: Record<string, string> = {
          [CanonicalTaskStatus.COMPLETED]: 'Task completed',
          [CanonicalTaskStatus.NOT_APPLICABLE]: 'Task marked N/A',
          [CanonicalTaskStatus.PENDING]: 'Task reopened',
        };
        toast.success(messages[newStatus] || 'Task updated');
      } catch (error) {
        console.error('Failed to update task status:', error);
        toast.error('Failed to update task status');
      }
    },
    [matterId, updateTask, completedStatus, pendingStatus, notApplicableStatus],
  );

  const handleTaskClick = useCallback(
    (taskId: string) => {
      if (onTaskClickProp) {
        onTaskClickProp(taskId);
      }
    },
    [onTaskClickProp],
  );

  if (isLoading || isStaffLoading) {
    return (
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3">
        <div className="flex items-center gap-2">
          <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-zinc-300 dark:border-zinc-600 border-t-zinc-600 dark:border-t-zinc-400" />
          <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
            Loading...
          </span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
        <span className="text-xs font-medium text-red-600 dark:text-red-400">
          Failed to load tasks
        </span>
      </div>
    );
  }

  if (allTasks.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 text-center">
        <ClipboardList className="mx-auto h-4 w-4 text-zinc-300 dark:text-zinc-600" />
        <p className="mt-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-400">
          No tasks
        </p>
      </div>
    );
  }

  // Count unassigned (excluding completed and N/A)
  const unassignedCount = allTasks.filter(
    (t) =>
      !t.assignee &&
      t.status?.canonical_status !== CanonicalTaskStatus.COMPLETED &&
      t.status?.canonical_status !== CanonicalTaskStatus.NOT_APPLICABLE,
  ).length;

  // Count active (excluding completed and N/A)
  const activeCount = allTasks.filter(
    (t) =>
      t.status?.canonical_status !== CanonicalTaskStatus.COMPLETED &&
      t.status?.canonical_status !== CanonicalTaskStatus.NOT_APPLICABLE,
  ).length;

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-3 py-2 bg-white dark:bg-zinc-900">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-3.5 w-3.5 text-zinc-500 dark:text-zinc-400" />
          <h3 className="text-xs font-semibold text-zinc-900 dark:text-white">
            Tasks
          </h3>
          <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
            {activeCount}/{allTasks.length}
          </span>
        </div>
        {unassignedCount > 0 && (
          <span className="text-xs font-medium text-amber-600 dark:text-amber-500">
            {unassignedCount} unassigned
          </span>
        )}
      </div>

      {/* Grouped task list */}
      <div>
        {sortedGroups.map(([groupName, tasks]) => (
          <TaskGroupSection
            key={groupName}
            groupName={groupName}
            tasks={tasks}
            isOpen={openGroups[groupName] ?? false}
            onToggle={() => handleToggleGroup(groupName)}
            staffUsers={staffUsers}
            onAssigneeChange={handleAssigneeChange}
            onDueDateChange={handleDueDateChange}
            onPriorityChange={handlePriorityChange}
            onStatusChange={handleStatusChange}
            onTaskClick={handleTaskClick}
            onDocumentClick={onDocumentClick}
            toggleInCallPrep={toggleInCallPrep}
            isInCallPrep={isInCallPrep}
            deliverablesByTaskId={deliverablesByTaskId}
          />
        ))}
      </div>
    </div>
  );
};
